/**
 * Common utility functions
 */

// Format currency in Brazilian Real
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Format date in Brazilian format
export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('pt-BR');
};

// Format datetime in Brazilian format
export const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('pt-BR');
};

// Format phone number
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Generate unique ID
export const generateId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Order status helpers
export const ORDER_STATUSES = {
  pendente: { label: 'Pendente', color: 'yellow', icon: '⏳' },
  confirmado: { label: 'Confirmado', color: 'blue', icon: '✅' },
  preparando: { label: 'Preparando', color: 'orange', icon: '👨‍🍳' },
  em_producao: { label: 'Em Produção', color: 'orange', icon: '🔥' },
  pronto: { label: 'Pronto', color: 'green', icon: '🎉' },
  saiu_entrega: { label: 'Saiu para Entrega', color: 'purple', icon: '🛵' },
  entregue: { label: 'Entregue', color: 'green', icon: '✅' },
  cancelado: { label: 'Cancelado', color: 'red', icon: '❌' }
};

export const getStatusLabel = (status) => {
  return ORDER_STATUSES[status]?.label || status;
};

export const getStatusColor = (status) => {
  return ORDER_STATUSES[status]?.color || 'gray';
};

export const getStatusIcon = (status) => {
  return ORDER_STATUSES[status]?.icon || '📦';
};

// Payment method helpers
export const PAYMENT_METHODS = {
  dinheiro: { label: 'Dinheiro', icon: '💵' },
  cartao: { label: 'Cartão', icon: '💳' },
  pix: { label: 'PIX', icon: '🔑' },
  pix_mp: { label: 'PIX (Mercado Pago)', icon: '🚀' },
  vale_refeicao: { label: 'Vale Refeição', icon: '🍽️' },
  vale_alimentacao: { label: 'Vale Alimentação', icon: '🛒' }
};

export const getPaymentLabel = (method) => {
  return PAYMENT_METHODS[method]?.label || method;
};

export const getPaymentIcon = (method) => {
  return PAYMENT_METHODS[method]?.icon || '💰';
};

// Delivery type helpers
export const DELIVERY_TYPES = {
  delivery: { label: 'Delivery', icon: '🛵' },
  retirada: { label: 'Retirada', icon: '🏃' },
  mesa: { label: 'Mesa', icon: '🍽️' }
};

export const getDeliveryLabel = (type) => {
  return DELIVERY_TYPES[type]?.label || type;
};

export const getDeliveryIcon = (type) => {
  return DELIVERY_TYPES[type]?.icon || '📦';
};

// Color options for themes
export const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e'
];

// ==================== BUSCA FUZZY (tolerante a acentos e erros de digitação) ====================

// Normaliza: remove acentos, minúsculo, espaços extras
export const normalizeText = (str) => {
  return (str == null ? '' : String(str))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

// Distância de Levenshtein
export const levenshteinDistance = (a, b) => {
  a = a || ''; b = b || '';
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
};

// Match flexível: ignora acentos/caixa e tolera erros de digitação por palavra
export const fuzzyMatch = (query, target) => {
  const q = normalizeText(query);
  if (!q) return true;
  const t = normalizeText(target);
  if (!t) return false;
  if (t.includes(q)) return true;
  const targetTokens = t.split(' ').filter(Boolean);
  const queryTokens = q.split(' ').filter(Boolean);
  return queryTokens.every((qt) => {
    if (t.includes(qt)) return true;
    return targetTokens.some((tt) => {
      if (tt.includes(qt) || qt.includes(tt)) return true;
      const tolerance = qt.length <= 4 ? 1 : qt.length <= 7 ? 2 : 3;
      return levenshteinDistance(qt, tt) <= tolerance;
    });
  });
};

